<template>

    <div>
        <CoolLightBox v-if="photos.length" :items="photos"
                      :key="photos.length"
                      :index="index"
                      @close="index = null">
        </CoolLightBox>

        <strong v-if="label">
            {{ label }}
        </strong>
        <div v-if="(maxFiles || maxSize) && canUpdate">
            <div class="text-secondary small">
                    <div>
                        <span v-if="maxFiles && remainingFilesCount">
                            Вы можете загрузить еще {{ remainingFilesCount }} {{ fileDeclension }}.
                        </span>
                        <span v-if="maxFiles && (remainingFilesCount === 0)">
                            Загружено максимально допустимое количество файлов ({{ maxFiles }}).
                        </span>
                    </div>

                    <div>
                        <span v-if="remainingSize">
                            Общий размер файлов, доступный к загрузке - {{ remainingSizeMegabytes }} МБ.
                        </span>
                    </div>

                    <div v-if="excess.length" class="mt-2">
                        <span>Не удалось загрузить следующие файлы из-за ограничения на количество:</span>
                        <ul class="small">
                            <li v-for="fileName in excess" :key="fileName">{{ fileName }}</li>
                        </ul>
                    </div>

                    <div v-if="mismatched.length" class="mt-2">
                        <span>Не удалось загрузить следующие файлы из-за типа:</span>
                        <ul class="small">
                            <li v-for="fileName in mismatched" :key="fileName">{{ fileName }}</li>
                        </ul>
                    </div>

                    <div v-if="sizeExcess.length" class="mt-2">
                        <span>Не удалось загрузить следующие файлы из-за превышения лимита по размеру:</span>
                        <ul class="small">
                            <li v-for="fileName in sizeExcess" :key="fileName">{{ fileName }}</li>
                        </ul>
                    </div>

            </div>
        </div>

        <div class="d-flex flex-wrap mt-2">
            <gallery-thumbnail v-for="(item, i) in allItems"
                               :file="item"
                               :key="i"
                               :api-url="rootApiUrl"
                               :removable="canUpdate"
                               :width="120"
                               :height="100"
                               :has-file-name-input="hasFileNameInputs"
                               :delete-on-remove="deleteOnRemove"
                               class="ml-0 mr-2 mb-2"
                               @click.native="onClick(item.url, i)"
                               @file-removed="onRemoved"
                               @failed-file-removed="onRemoved"
                               @gallery-item-name-changed="onItemNameChanged(item)"
            >
            </gallery-thumbnail>
            <gallery-add-thumbnail v-if="canUpdate"
                                   v-show="canAdd"
                                   :kind="kind"
                                   :initial-upload-url="rootApiUrl"
                                   :max-files="remainingFilesCount"
                                   :max-size="remainingSize"
                                   :disabled="!canAdd"
                                   :accepts="accepts"
                                   :upload-immediately="uploadImmediately"
                                   :width="120"
                                   :height="100"
                                   ref="addThumbnail"
                                   @file-started-uploading="onFileStartedUploading"
                                   @file-uploaded="onFileUploaded"
                                   @files-excess="onFilesExcess"
                                   @files-type-mismatched="onMismatchedTypes"
                                   @files-size-exceeded="onFilesSizeExcess"
                                   @file-buffered="onFileBuffered">
            </gallery-add-thumbnail>
        </div>

        <div v-if="!items.length && !canUpdate">
            <b-card >
                <slot name="noContent">
                    <span class="text-secondary">Нет загруженных файлов.</span>
                </slot>
            </b-card>
        </div>

    </div>

</template>

<script>
    import CoolLightBox from 'vue-cool-lightbox';
    import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'
    import GalleryThumbnail from "./GalleryThumbnail";
    import httpClient from "../../http";
    import GalleryAddThumbnail from "./GalleryAddThumbnail";
    import { UploadedFile } from "./file";

    export default {
        name: "Gallery",
        components: {
            GalleryAddThumbnail,
            CoolLightBox,
            GalleryThumbnail
        },
        props: {
            label: { type: String, default: null },
            rootApiUrl: { type: String, default: null },
            canUpdate: { type: Boolean, default: true },
            maxFiles: { type: Number  },
            maxSize: { type: Number },
            accepts: { type: Array, default: () => { return [] } },
            kind: { type: Number, default: 2},
            uploadImmediately: { type: Boolean, default: true },
            existing: { type: Boolean, default: false },
            hasFileNameInputs: { type: Boolean, default: false },
            deleteOnRemove: { type: Boolean, default: true },
        },
        computed: {
            uploadedGalleryUrl() {
                return this.rootApiUrl ? `${this.rootApiUrl}?kind=${this.kind}` : null;
            },
            allItems() {
                let allItems = [];
                let itemUuids = new Set();

                for (const item of this.items) {
                    allItems.push(item);
                    itemUuids.add(item.uuid);
                }

                for (const bufferedItem of this.bufferedItems) {
                    if (!itemUuids.has(bufferedItem.uuid)) {
                        allItems.push(bufferedItem)
                    }
                }

                return allItems
            },
            photos() {
                let photos = [];
                for (const i in this.allItems) {
                    const url = this.allItems[i].url;
                    if (url) {
                        photos.push(url)
                    }
                }
                return photos
            },
            maxSizeBytes() {
                if (this.maxSize) {return this.maxSize * 1024 * 1024 }
                return 0;
            },
            remainingSize() {
                if (this.maxSizeBytes) {
                    let size = 0;
                    for (let i in this.allItems) {
                        size += this.allItems[i].size
                    }
                    return this.maxSizeBytes - size;
                }
                return 0;
            },
            remainingSizeMegabytes() {
                if (this.maxSizeBytes) {
                    return Math.round(this.remainingSize / (1024 * 1024) * 10) / 10
                }
                return 0;
            },
            remainingFilesCount() {
                if (this.maxFiles) {
                    return Math.max(0, this.maxFiles - this.allItems.length)
                }
                return 0;
            },
            fileDeclension() {
                let tail = this.remainingFilesCount % 10;
                if (tail === 0) { return 'файлов'; }
                if (tail < 5) {
                    let second = (this.remainingFilesCount - tail) / 10 % 10;
                    if (second === 1) { return 'файлов'; }
                    if (tail === 1) { return 'файл' }
                    return 'файла'
                }
                return 'файлов';
            },
            canAdd() { return this.canUpdate && (!this.maxFiles || this.maxFiles && this.remainingFilesCount > 0) }
        },
        data() {
            return {
                items: [],
                bufferedItems: [],
                index: null,
                excess: [],
                mismatched: [],
                sizeExcess: []
            }
        },
        methods: {
            resetExcess() { this.excess = [] },
            resetMismatched() { this.mismatched = [] },
            resetSizeExcess() { this.sizeExcess = [] },
            resetMessagesAndItems() {
                this.resetExcess();
                this.resetMismatched();
                this.resetSizeExcess();
                this.bufferedItems = [];
                this.items = [];
            },
            onClick(photo, index) {
                this.index = index;
            },
            onFileStartedUploading(file) {
                file.uploading = true;
                this.items.push(file);
            },
            onFileUploaded(data) {
                let uploadedIds = [];
                for (let i = 0; i < this.items.length; ++i) {
                    let processingItem = this.items[i];
                    if (processingItem.uuid === data.uuid) {
                        processingItem.uploading = false;
                        if (data.response !== undefined) {
                            processingItem.url = data.response['url'];
                            processingItem.id = data.response['id'];
                            processingItem.uploading = false;
                            uploadedIds.push(processingItem.id)
                        } else {
                             processingItem.error = data.error;
                        }
                    }
                }
                this.$emit('files-uploaded', {ids: uploadedIds})
            },
            onRemoved(data) {
                this.resetExcess();
                this.resetMismatched();
                this.resetSizeExcess();

                const itemId = data.uuid;
                let idsToExclude = []

                for (let i = 0; i < this.items.length; i++) {
                    if (this.items[i] && this.items[i].uuid === itemId) {
                        if (this.items[i].id) {
                            idsToExclude.push(this.items[i].id)
                        }
                        this.items.splice(i, 1);
                        break;
                    }
                }

                for (let i = 0; i < this.bufferedItems.length; i++) {
                    if (this.bufferedItems[i] && this.bufferedItems[i].uuid === itemId) {
                        if (this.bufferedItems[i].id) {
                            idsToExclude.push(this.bufferedItems[i].id)
                        }
                        this.bufferedItems.splice(i, 1);
                        break;
                    }
                }
                this.$emit('files-removed', {ids: idsToExclude})
            },
            onAttachmentIdsRemoved(ids) {
                this.resetExcess();
                this.resetMismatched();
                this.resetSizeExcess();

                for (let i = 0; i < this.items.length; i++) {
                    if (this.items[i] && this.items[i].id && ids.indexOf(this.items[i].id) >= 0) {
                        this.items.splice(i, 1);
                        if (this.items.length > 0) {
                            i--;
                        }
                    }
                }

                for (let i = 0; i < this.bufferedItems.length; i++) {
                    if (this.bufferedItems[i] && this.bufferedItems[i].id && ids.indexOf(this.bufferedItems[i].id) >= 0) {
                        this.bufferedItems.splice(i, 1);
                        if (this.items.length > 0) {
                            i--;
                        }
                    }
                }
                this.$emit('files-removed', {ids: ids})
            },
            onFilesExcess(data) {
                this.excess = [...data.fileNames];
            },
            onMismatchedTypes(data) {
                this.mismatched = [...data.fileNames];
            },
            onFilesSizeExcess(data) {
                this.sizeExcess = [...data.fileNames];
            },
            onFileBuffered(data) {
                this.bufferedItems.push(data);
            },
            onItemNameChanged(item) {
                this.$emit('gallery-item-name-changed')
            }
        },
        mounted() {
            if (this.uploadedGalleryUrl && this.existing) {
                const vm = this;
                httpClient.get(this.uploadedGalleryUrl)
                    .then(response => {
                        for (const i in response.data['results']) {
                            const file = response.data['results'][i];
                            let item = new UploadedFile(file['url'], file['id'], null,
                                null, false, file['size'], true, file['name']);
                            vm.items.push(item)
                        }
                    })
                    .catch(reason => {
                        vm.$bvToast.toast('Не удалось получить файлы',
                            {title: 'Ошибка при загрузке файлов', autoHideDelay: 5000, variant: 'danger'});
                    })
            }

        }

    }
</script>

<style scoped lang="scss">
    .rounded-corners {
        border-radius: 8px ;
    }
</style>